import { format, addDays } from 'date-fns';
import { todayLocalISO, ymdToNoonUtcIso } from '../../utils/dateUtils';
import { roundDollars } from '../../utils/money';

/**
 * Pure data helpers for the Run Barn Operation page. Extracted from
 * BarnOperations.tsx so the billing-critical math (which horse gets which
 * service, what totals show, how drafts migrate) is unit-testable without
 * the 1300-line page component.
 *
 * Since 2026-08 each line item carries its OWN horse list (`horseIds`),
 * snapshotted from the selection at add-time — the roster checkboxes only
 * pick targets for the NEXT item. The backend has supported per-horse items
 * all along (BarnOperationsDto.perHorseItems, used by the barn-visit
 * workspace); this page simply materializes into that shape on submit.
 */

export type ServiceType = 'procedure' | 'vaccination' | 'medication';

export interface LineItemData {
  serviceType: ServiceType;
  serviceName: string;
  unitPrice: number | '';
  notes: string;
  category: string;
  nextDueDate: string;
  dosage: string;
  quantity: number | '';
  instructions: string;
  endDate: string;
  // Bottle (ControlledSubstanceLot.id) for the DEA auto-log when the name
  // matches the clinic's CS registry; '' = server FIFO pick (ADR-0014).
  deaLotId: string;
}

/** A line item bound to the horses it bills (selection snapshot at add-time). */
export interface AssignedLineItem extends LineItemData {
  horseIds: string[];
}

export interface SharedFormData {
  date: string;
  performedBy: string;
  dueDate: string;
  invoiceDescription: string;
}

export const initialLineItem: LineItemData = {
  serviceType: 'procedure',
  serviceName: '',
  unitPrice: '',
  notes: '',
  category: '',
  nextDueDate: '',
  dosage: '',
  quantity: '',
  instructions: '',
  endDate: '',
  deaLotId: '',
};

export const makeInitialSharedData = (): SharedFormData => ({
  date: todayLocalISO(),
  performedBy: '',
  dueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
  invoiceDescription: '',
});

/**
 * The quantity a line actually bills at. Medication-only by convention
 * (matches BarnVisitWorkspace + the payload builder, which omits `quantity`
 * for other types — the backend multiplies whatever it receives, so a stale
 * qty left over from a service-type switch must not leak into totals).
 */
export const getBilledQty = (item: LineItemData): number =>
  item.serviceType === 'medication' && typeof item.quantity === 'number' && item.quantity > 0
    ? item.quantity
    : 1;

/** Pre-tax line total (unit price × billed qty), rounded to the cent like every line producer (INV-112). */
export const getLineTotal = (item: LineItemData): number => {
  const price = typeof item.unitPrice === 'number' ? item.unitPrice : 0;
  return roundDollars(price * getBilledQty(item));
};

/**
 * The wire shape POST /billing/barn-operations expects per item
 * (BarnOperationItemDto). Kept beside the totals math so the preview and the
 * payload can't drift: anything getLineTotal counts, this sends.
 */
export const buildItemPayload = (
  item: LineItemData,
  dispenseLocationId: string,
): Record<string, unknown> => {
  const mapped: Record<string, unknown> = {
    serviceType: item.serviceType,
    serviceName: item.serviceName,
  };
  if (item.unitPrice !== '') mapped.unitPrice = Number(item.unitPrice);
  if (item.notes) mapped.notes = item.notes;
  if (item.category) mapped.category = item.category;
  if (item.serviceType === 'vaccination' && item.nextDueDate) {
    // CALENDAR day from a `type="date"` field — noon UTC, not the UTC midnight
    // `new Date(ymd).toISOString()` used to produce (DATE-KINDS §2.5 W1/W10:
    // `Vaccination.nextDueDate` carried two anchors, this one and the MUI
    // DatePicker's browser-local midnight in VaccinationDialog).
    mapped.nextDueDate = ymdToNoonUtcIso(item.nextDueDate);
  }
  if (item.serviceType === 'medication') {
    if (item.dosage) mapped.dosage = item.dosage;
    if (item.quantity !== '') mapped.quantity = Number(item.quantity);
    if (item.instructions) mapped.instructions = item.instructions;
    // Same anchor fix for `Medication.endDate` (the other two-anchor column).
    if (item.endDate) mapped.endDate = ymdToNoonUtcIso(item.endDate);
  }
  // "Deduct stock from" (run-level) — omitted = Automatic (server infers:
  // vet's truck if assigned, else warehouse — ADR-0013). The DTO carries the
  // field per-item.
  if (dispenseLocationId) mapped.dispenseLocationId = dispenseLocationId;
  // Bottle for the DEA auto-log (per-item); omitted = server FIFO pick.
  if (item.deaLotId) mapped.deaLotId = item.deaLotId;
  return mapped;
};

export interface HorseItemsEntry<T extends AssignedLineItem = AssignedLineItem> {
  horseId: string;
  items: T[];
}

/**
 * Group items by the horses they're assigned to, in roster order, dropping
 * horses with nothing assigned — the exact shape `perHorseItems` wants.
 */
export function materializePerHorseItems<T extends AssignedLineItem>(
  items: T[],
  horses: Array<{ id: string }>,
): HorseItemsEntry<T>[] {
  return horses
    .map((h) => ({ horseId: h.id, items: items.filter((it) => it.horseIds.includes(h.id)) }))
    .filter((entry) => entry.items.length > 0);
}

/** Pre-tax total across every horse's assigned items. */
export function estimateOperationTotal(
  items: AssignedLineItem[],
  horses: Array<{ id: string }>,
): number {
  return materializePerHorseItems(items, horses).reduce(
    (sum, entry) => sum + entry.items.reduce((s, it) => s + getLineTotal(it), 0),
    0,
  );
}

/**
 * Drop roster-departed horses from every item's assignment. Returns the SAME
 * array reference when nothing changed so a state setter can no-op (avoids
 * an effect/render loop).
 */
export function pruneItemHorseIds<T extends AssignedLineItem>(
  items: T[],
  rosterIds: Set<string>,
): T[] {
  let changed = false;
  const next = items.map((item) => {
    const kept = item.horseIds.filter((id) => rosterIds.has(id));
    if (kept.length === item.horseIds.length) return item;
    changed = true;
    return { ...item, horseIds: kept };
  });
  return changed ? next : items;
}

// ── Draft persistence ────────────────────────────────────────────────────────
// In-progress work is drafted to localStorage so navigating away doesn't lose
// it. v2 adds per-item horseIds; a legacy (unversioned) draft's items are
// migrated onto the draft's selection — faithful, because under the old
// semantics those items would have billed exactly that selection.

export const DRAFT_STORAGE_KEY = 'barn-operations-draft';

export interface OperationDraft {
  version: 2;
  selectedBarn: string | null;
  selectedHorseIds: string[];
  items: AssignedLineItem[];
  currentItem: LineItemData;
  sharedData: SharedFormData;
}

export function parseOperationDraft(raw: string | null): OperationDraft | null {
  if (!raw) return null;
  try {
    const draft = JSON.parse(raw);
    if (!draft || typeof draft !== 'object') return null;
    if (draft.version === 2) {
      // Minimal shape check — a tampered draft must not crash the page mount.
      if (!Array.isArray(draft.items)) return null;
      return {
        ...draft,
        items: draft.items.map((it: AssignedLineItem) => ({
          ...it,
          horseIds: Array.isArray(it?.horseIds) ? it.horseIds : [],
        })),
      } as OperationDraft;
    }
    if (draft.version !== undefined) return null; // future/unknown version — discard
    // Legacy draft (no version): bind each item to the drafted selection.
    const selection: string[] = Array.isArray(draft.selectedHorseIds) ? draft.selectedHorseIds : [];
    return {
      version: 2,
      selectedBarn: draft.selectedBarn ?? null,
      selectedHorseIds: selection,
      items: (Array.isArray(draft.items) ? draft.items : []).map((it: LineItemData) => ({
        ...it,
        horseIds: [...selection],
      })),
      currentItem: draft.currentItem ?? initialLineItem,
      sharedData: draft.sharedData ?? makeInitialSharedData(),
    };
  } catch {
    return null;
  }
}

/** The vaccines on a run and the horses each reaches, in run order, for the lot step (spec §18, ADR-0047). */
export const vaccineLinesOf = (
  workList: Array<{ horseId: string; items: Array<{ serviceType: string; serviceName: string }> }>,
  horses: Array<{ id: string; name: string }>,
): Array<{ name: string; horses: Array<{ id: string; name: string }> }> => {
  const nameOf = new Map(horses.map((h) => [h.id, h.name]));
  const reached = new Map<string, Array<{ id: string; name: string }>>();
  for (const entry of workList) {
    const names = new Set(entry.items.filter((i) => i.serviceType === 'vaccination' && i.serviceName).map((i) => i.serviceName));
    names.forEach((name) => reached.set(name, [...(reached.get(name) ?? []), { id: entry.horseId, name: nameOf.get(entry.horseId) ?? '' }]));
  }
  return Array.from(reached, ([name, list]) => ({ name, horses: list }));
};

/** Each horse's vaccine line takes its own lot and dose; the server records them on that horse's vaccination. */
export const withVaccineLots = (
  perHorseItems: Array<{ horseId: string; items: Array<Record<string, unknown>> }>,
  lots: Record<string, Record<string, Record<string, unknown>>>,
) =>
  perHorseItems.map((entry) => ({
    ...entry,
    items: entry.items.map((item) => {
      const lot = item.serviceType === 'vaccination' && typeof item.serviceName === 'string' ? lots[item.serviceName]?.[entry.horseId] : undefined;
      return lot ? { ...item, ...Object.fromEntries(Object.entries(lot).filter(([, v]) => v != null)) } : item;
    }),
  }));
